import React, { useEffect, useMemo, useRef } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faRotateRight,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import type { TableModel } from "utils/tableModel"
import { omittedNote } from "features/Dashboard/runtime/modelAdapters"
import {
  PanelRuntime,
  useDashboardStore,
} from "features/Dashboard/runtime/dashboardStore"
import { orderedPanels, RepeatTile } from "features/Dashboard/runtime/panels"
import {
  buildTileView,
  effectiveStatistic,
} from "features/Dashboard/runtime/tileView"
import BarTile from "./BarTile.component"
import LineTile from "./LineTile.component"
import PieTile from "./PieTile.component"
import {
  panelGrid,
  panelLegendItems,
  panelOmitted,
  sharedAxisTop,
  showsPanelLegend,
} from "./panelCharts"

/** Columns when the tile names none (a panel set left over from a repeat). */
const DEFAULT_COLUMNS = 3

const MiniChart: React.FC<{
  tile: RepeatTile
  view: TableModel
  raw: TableModel | undefined
  axisMax: number | undefined
  /** The panel's category, whose own table a trace follows. */
  panel: string
}> = ({ tile, view, raw = view, axisMax, panel }) => {
  switch (tile.type) {
    case "bar":
      return (
        <BarTile
          tile={tile}
          model={view}
          raw={raw}
          compact
          axisMax={axisMax}
          panel={panel}
        />
      )
    case "line":
      return (
        <LineTile
          tile={tile}
          model={view}
          raw={raw}
          compact
          axisMax={axisMax}
          panel={panel}
        />
      )
    case "pie":
      return (
        <PieTile tile={tile} model={view} raw={raw} compact panel={panel} />
      )
  }
}

/**
 * A repeated tile: one mini chart per category of its repeat variable, in
 * the tile's number of columns, each titled by its category, with one
 * legend under the grid and one value-axis end for every panel so the
 * panels read against each other. Each panel draws its view (see
 * `buildTileView`). While a panel's next run is under way it keeps drawing
 * its last view, if it has one; a panel that failed shows its error in its
 * own cell and leaves the others be, and a line under the grid offers
 * Retry, which plans the tile again (only the failed panels run: the
 * others come from the cache). Rows the charts leave out for being in
 * another unit are named once, under the grid.
 */
const PanelGrid: React.FC<{
  tile: RepeatTile
  panels: Record<string, PanelRuntime>
}> = ({ tile, panels }) => {
  const codebook = useDashboardStore((s) => s.codebook)
  const controls = useDashboardStore((s) => s.tileControls[tile.id])
  const palette = useDashboardStore((s) => s.document.theme.chartPalette)
  const colourMap = useDashboardStore((s) => s.document.theme.categoryColours)
  const retryTile = useDashboardStore((s) => s.retryTile)
  const variable = tile.repeatBy?.variable
  const ordered = useMemo(
    () => orderedPanels(panels, codebook, variable),
    [panels, codebook, variable]
  )
  const views = useMemo(
    () =>
      ordered.map(({ panel }) =>
        panel.model ? buildTileView(tile, panel.model, controls) : undefined
      ),
    [ordered, tile, controls]
  )
  // The last view each panel drew, shown while its next run is under way.
  const lastViews = useRef<Record<string, TableModel>>({})
  useEffect(() => {
    ordered.forEach(({ category }, i) => {
      const view = views[i]
      if (view) lastViews.current[category] = view
    })
  }, [ordered, views])

  const shown = ordered.map(({ category, panel }, i) =>
    panel.error !== undefined
      ? undefined
      : (views[i] ?? lastViews.current[category])
  )
  const drawn = shown.filter((v): v is TableModel => v !== undefined)
  const statistic = effectiveStatistic(tile, controls)
  const axisMax = sharedAxisTop(tile, drawn, statistic)
  // Colours follow a last result, not a view (every panel has the same
  // rows and columns).
  const raw = ordered.find(({ panel }) => panel.model)?.panel.model
  const legend = drawn[0]
    ? panelLegendItems(tile, drawn[0], raw ?? drawn[0], statistic, palette, colourMap)
    : []
  const note = omittedNote(
    drawn[0] ? panelOmitted(tile, drawn[0], statistic) : []
  )
  const failed = ordered.filter(({ panel }) => panel.error !== undefined).length
  const { columns, rows } = panelGrid(
    ordered.length,
    tile.repeatBy?.columns ?? DEFAULT_COLUMNS
  )

  return (
    <div className="dq-panels">
      <div
        className="dq-panels__grid"
        style={
          {
            "--dq-panel-cols": columns,
            "--dq-panel-rows": rows,
          } as React.CSSProperties
        }
      >
        {ordered.map(({ category, panel }, i) => {
          const view = shown[i]
          return (
            <figure
              key={category}
              className="dq-panel"
              data-pending={!panel.model && panel.error === undefined}
            >
              <figcaption className="dq-panel__title" title={category}>
                {category}
              </figcaption>
              <div className="dq-panel__body">
                {panel.error !== undefined ? (
                  <p className="dq-panel__error">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    <span>{panel.error}</span>
                  </p>
                ) : view ? (
                  <MiniChart
                    tile={tile}
                    view={view}
                    raw={panel.model ?? raw}
                    axisMax={axisMax}
                    panel={category}
                  />
                ) : (
                  <div className="dq-tile__skeleton" aria-busy="true" />
                )}
              </div>
            </figure>
          )
        })}
      </div>
      {showsPanelLegend(tile, legend) && (
        <ul
          className="dq-panels__legend"
          aria-label="Legend"
          data-mark={tile.type === "line" ? "line" : "block"}
        >
          {legend.map((item) => (
            <li key={item.id} className="dq-panels__item">
              <span
                className="dq-panels__key"
                style={{ background: item.color }}
                aria-hidden
              />
              <span className="dq-panels__label">{item.label}</span>
            </li>
          ))}
        </ul>
      )}
      {note && (
        <p className="dq-chart__omitted" title={note}>
          {note}
        </p>
      )}
      {failed > 0 && (
        <div className="dq-panels__failed" role="status">
          <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden />
          <span>
            {failed === 1
              ? "1 panel failed."
              : `${failed} panels failed.`}
          </span>
          <button
            type="button"
            className="dq-panels__retry"
            onClick={() => retryTile(tile.id)}
          >
            <FontAwesomeIcon icon={faRotateRight} aria-hidden />
            Retry
          </button>
        </div>
      )}
    </div>
  )
}

export default PanelGrid
